import { Edge, Node, Position } from 'reactflow';
import { Key } from 'ts-key-enum';
import isEmpty from 'lodash/isEmpty';
import React, {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';
import sortBy from 'lodash/sortBy';
import { IPolicyMetadata } from '@tymely/atoms';
import { conditionValidator } from '@tymely/features';
import { useGraphPolicyArgumentsMetadataQuery } from '@tymely/pages/GraphPolicyEditor/hooks/useGraphPolicyArgumentsMetadataQuery';
import { getAncestors, getDescendants, Graph, useKeys, useUndo } from '@tymely/utils';
import { createDecisionNode } from '@tymely/pages/GraphPolicyEditor/utils/nodes';

import { EdgeData, NodeData, PolicyGraph } from '../types/graph';
import layoutGraph, { LayoutOption } from '../utils/layout';

type SetGraphOptions = { checkpoint?: boolean };
type NodeId = PolicyGraph['nodes'][number]['id'];

const prepareEdges = (graph: Graph<NodeData, EdgeData>, layout: LayoutOption) => {
    const horizontal = layout === 'horizontal';
    const sources = new Map<string, number>();
    const targets = new Map<string, number>();
    const edgesBySourceTarget = graph.edges.reduce((acc, edge) => {
        const key = `${edge.source}\t${edge.target}`;
        acc.set(key, [...(acc.get(key) || []), edge]);
        return acc;
    }, new Map<string, Edge<EdgeData>[]>());
    const parallelRanks = new Map<string, { index: number; total: number }>();
    edgesBySourceTarget.forEach((edges) => {
        const sorted = sortBy(edges, (e) => e.data?.order ?? 0);
        sorted.forEach((edge, index) => {
            parallelRanks.set(edge.id, { index, total: sorted.length });
        });
    });
    const edgesBySource = graph.edges.reduce((acc, edge) => {
        acc.set(edge.source, [...(acc.get(edge.source) || []), edge]);
        return acc;
    }, new Map<string, Edge<EdgeData>[]>());

    return Array.from(edgesBySource.values()).flatMap((edges) =>
        sortBy(edges, (e) => e.data?.order ?? 0).map((edge, index) => {
            const rank = parallelRanks.get(edge.id) ?? { index: 0, total: 1 };
            const sourceHandleIndex = sources.get(edge.source) ?? 0;
            const targetHandleIndex = targets.get(edge.target) ?? 0;
            sources.set(edge.source, sourceHandleIndex + 1);
            targets.set(edge.target, targetHandleIndex + 1);
            return {
                ...edge,
                data: { ...edge.data, order: index, parallelIndex: rank.index, totalParallel: rank.total },
                type: layout,
                updatable: 'target',
                sourceHandle: `${horizontal ? Position.Right : Position.Bottom}-${sourceHandleIndex}`,
                targetHandle: `${Position.Left}-${targetHandleIndex}`,
            };
        }),
    );
};

const useGeneratePolicyEditor = () => {
    const layoutRef = useRef<LayoutOption>('horizontal');
    const [highlightedEdges, setHighlightedEdges] = useState<Set<string>>(new Set());
    const [highlightedNodes, setHighlightedNodes] = useState<Set<string>>(new Set());
    const [highlightedEdgesSimulated, setHighlightedEdgesSimulated] = useState<Set<string>>(new Set());
    const [highlightedNodesSimulated, setHighlightedNodesSimulated] = useState<Set<string>>(new Set());
    const [highlightedEdgesWf0, setHighlightedEdgesWf0] = useState<Set<string>>(new Set());
    const [highlightedNodesWf0, setHighlightedNodesWf0] = useState<Set<string>>(new Set());
    const [selectedNodeId, setSelectedNodeId] = useState<NodeId>();
    const [policyMetadata, setPolicyMetadata] = useState<IPolicyMetadata>();

    const [policyEditorState, policyEditorActions] = useUndo<PolicyGraph | undefined>(undefined, {
        useCheckpoints: true,
    });

    const set = (graph: PolicyGraph, { checkpoint = true }: SetGraphOptions = {}) => {
        const edges = prepareEdges(graph, layoutRef.current);
        policyEditorActions.set({ ...graph, edges: edges as Edge<EdgeData>[] }, checkpoint);
    };

    const setLayout = useCallback(
        async (
            selectedLayout?: LayoutOption,
            setNodes?: (nodes: Node<NodeData>[] | ((nodes: Node<NodeData>[]) => Node<NodeData>[])) => void,
            setEdges?: (edges: Edge<EdgeData>[] | ((edges: Edge<EdgeData>[]) => Edge<EdgeData>[])) => void,
            currentNodes?: Node<NodeData>[],
        ) => {
            if (!policyEditorState.present) {
                return;
            }
            try {
                const _layout = selectedLayout ?? layoutRef.current;
                const graph = await layoutGraph(policyEditorState.present, _layout, currentNodes);
                layoutRef.current = _layout;

                const preparedEdges = prepareEdges(graph, _layout) as Edge<EdgeData>[];

                if (setNodes && setEdges) {
                    setNodes((currentNodes) => {
                        const currentNodeMap = new Map(currentNodes.map((node) => [node.id, node]));
                        return graph.nodes.map((node) => {
                            const currentNode = currentNodeMap.get(node.id);
                            if (currentNode) {
                                const isGroupNode = currentNode.type === 'policyGroup';
                                const hasParent = !!currentNode.parentId;
                                const shouldUpdatePosition = isGroupNode || !hasParent;

                                return {
                                    ...currentNode,
                                    ...node,
                                    position: shouldUpdatePosition ? { ...node.position } : { ...currentNode.position },
                                    style: { ...node.style, ...currentNode.style },
                                    dragging: false,
                                };
                            }
                            return {
                                ...node,
                                position: { ...node.position },
                                dragging: false,
                            };
                        });
                    });
                    setEdges(() => {
                        return preparedEdges;
                    });
                } else {
                    set(graph);
                }
            } catch (error) {
                console.error('Failed to apply layout:', error);
            }
        },
        [policyEditorState.present, set],
    );

    const highlightEdges = (nodeIds: string[], setEdges: Dispatch<SetStateAction<Set<string>>>, isOrdered = false) => {
        const graph = policyEditorState.present;
        if (!graph) return;

        const edgeIds = new Set<string>();

        if (isOrdered) {
            nodeIds.slice(0, -1).forEach((source, i) => {
                const target = nodeIds[i + 1];
                const edge = graph.edges.find((e) => e.source === source && e.target === target);
                if (edge) {
                    edgeIds.add(edge.id);
                }
            });
        } else {
            graph.edges.forEach(({ id, source, target }) => {
                if (nodeIds.includes(source) && nodeIds.includes(target)) {
                    edgeIds.add(id);
                }
            });
        }

        setEdges(edgeIds);
    };

    const highlightNodes = (nodeIds: string[], setNodes: Dispatch<SetStateAction<Set<string>>>) => {
        setNodes(new Set(nodeIds));
    };

    const accumulateHighlightNodes = (nodeIds: string[], setNodes: Dispatch<SetStateAction<Set<string>>>) => {
        setNodes((prev) => new Set([...prev, ...nodeIds]));
    };

    const accumulateHighlightEdges = (
        nodeIds: string[],
        setEdges: Dispatch<SetStateAction<Set<string>>>,
        isOrdered = false,
    ) => {
        const graph = policyEditorState.present;
        if (!graph) return;

        const edgeIds = new Set<string>();

        if (isOrdered) {
            nodeIds.slice(0, -1).forEach((source, i) => {
                const target = nodeIds[i + 1];
                const edge = graph.edges.find((e) => e.source === source && e.target === target);
                if (edge) {
                    edgeIds.add(edge.id);
                }
            });
        } else {
            graph.edges.forEach(({ id, source, target }) => {
                if (nodeIds.includes(source) && nodeIds.includes(target)) {
                    edgeIds.add(id);
                }
            });
        }

        setEdges((prev) => new Set([...prev, ...edgeIds]));
    };

    const highlightNodeEdges = (
        nodeId: string,
        setEdges?: Dispatch<SetStateAction<Set<string>>>,
        setNodes?: Dispatch<SetStateAction<Set<string>>>,
        withDescendants = true,
    ) => {
        if (!policyEditorState.present) {
            return;
        }
        const graph = policyEditorState.present;
        const nodeIdsInPath = new Set(
            getAncestors(nodeId, graph)
                .concat(withDescendants ? getDescendants(nodeId, graph) : [])
                .map((node) => node.id)
                .concat(nodeId),
        );

        setNodes?.((prev) => new Set([...prev, ...nodeIdsInPath]));

        const edgeIds = new Set(
            graph.edges
                .filter(({ source, target }) => nodeIdsInPath.has(target) && nodeIdsInPath.has(source))
                .map(({ id }) => id),
        );

        setEdges && setEdges(edgeIds);
    };

    const reset = useCallback(() => {
        const earliestState = policyEditorState.past.shift() ?? policyEditorState.present;
        if (earliestState) {
            const edges = prepareEdges(earliestState, layoutRef.current);
            policyEditorActions.reset({ ...earliestState, edges });
        }
    }, [policyEditorState.past, policyEditorState.present]);

    const resetToCheckpoint = (graph: PolicyGraph) => {
        const edges = prepareEdges(graph, layoutRef.current);
        policyEditorActions.reset({ ...graph, edges });
    };

    const { argMdsById } = useGraphPolicyArgumentsMetadataQuery();

    const nodesById = useMemo(() => {
        if (!policyEditorState.present) {
            return {};
        }
        return Object.fromEntries(policyEditorState.present.nodes.map((node) => [node.id, node]));
    }, [policyEditorState.present]);

    const errors = useMemo(() => {
        if (!policyEditorState.present) {
            return null;
        }
        const errors = policyEditorState.present.nodes
            .map((node) => {
                if (node.data.kind === 'arg' && !node.data.arg_metadata_id) {
                    return [node.id, 'Argument must be selected'];
                }
                return false;
            })
            .concat(
                policyEditorState.present.edges.map(({ data, source }) => {
                    const sourceNode = nodesById[source];
                    if (sourceNode?.data.kind === 'decision') {
                        return false;
                    }
                    const argMd = argMdsById[sourceNode.data.arg_metadata_id ?? -1];
                    if (data?.condition && argMd) {
                        const validator = conditionValidator(data.condition.predicate, argMd);
                        const validationResult = validator.safeParse(data.condition.value);
                        if (validationResult.success) {
                            return false;
                        }
                        return [sourceNode.id, validationResult.error.message];
                    }
                    return false;
                }),
            )
            .filter(Boolean);
        if (errors.length > 0) {
            return Object.fromEntries(errors);
        }
        return null;
    }, [policyEditorState.present, argMdsById]);

    const isValid = isEmpty(errors);

    useKeys([Key.Control, 'KeyZ'], policyEditorActions.undo);
    useKeys([Key.Control, 'KeyY'], policyEditorActions.redo);

    const { edgesBySource, edgesByTarget } = useMemo(() => {
        const edgesBySource: Record<string, Edge<EdgeData>[]> = {};
        const edgesByTarget: Record<string, Edge<EdgeData>[]> = {};
        if (policyEditorState.present) {
            policyEditorState.present.edges.forEach((edge) => {
                edgesBySource[edge.source] = [...(edgesBySource[edge.source] || []), edge];
                edgesByTarget[edge.target] = [...(edgesByTarget[edge.target] || []), edge];
            });
        }
        return { edgesByTarget, edgesBySource };
    }, [policyEditorState.present]);

    const findEdgesBetweenNodes = (nodes: Set<string>, edges: Edge<EdgeData>[]) => {
        return edges.filter((edge) => nodes.has(edge.source) && nodes.has(edge.target)).map((edge) => edge.id);
    };

    const selectNodeId = useCallback(
        (
            nodeId?: NodeId,
            isShiftPressed = false,
            setEdges?: Dispatch<SetStateAction<Set<string>>>,
            setNodes?: Dispatch<SetStateAction<Set<string>>>,
            highlightEdges = true,
            withDescendants = true,
        ) => {
            if (isShiftPressed && !nodeId) return;

            if (!isShiftPressed) {
                setSelectedNodeId(nodeId);
                setNodes?.(new Set(nodeId ? [nodeId] : []));

                if (highlightEdges) {
                    if (nodeId) {
                        highlightNodeEdges(nodeId, setEdges, setNodes, withDescendants);
                    } else {
                        setEdges?.(new Set());
                    }
                }
            } else if (nodeId) {
                setNodes?.((prev) => {
                    const newSet = new Set(prev);
                    if (newSet.has(nodeId)) {
                        newSet.delete(nodeId);
                    } else {
                        newSet.add(nodeId);
                    }
                    if (policyEditorState.present) {
                        const newEdges = findEdgesBetweenNodes(newSet, policyEditorState.present.edges);
                        setEdges?.(new Set(newEdges));
                    }
                    return newSet;
                });
            }
        },
        [setSelectedNodeId, highlightNodeEdges, policyEditorState.present],
    );

    const highlightEdgeById = (edgeId: string) => {
        setHighlightedEdges(new Set([edgeId]));
    };

    const convertNodeToDecision = useCallback(
        (nodeId: NodeId) => {
            const graph = policyEditorState.present;
            if (!graph) {
                return;
            }
            const nodes = graph.nodes.map((_node) => (_node.id === nodeId ? createDecisionNode(_node) : _node));
            set({ ...graph, nodes });
        },
        [policyEditorState.present],
    );

    return {
        state: policyEditorState.present,
        set,
        undo: policyEditorActions.undo,
        redo: policyEditorActions.redo,
        hasUnsavedChanges: policyEditorActions.canUndo,
        reset,
        resetToCheckpoint,
        isValid,
        errors,
        highlightedEdges,
        highlightEdges: (nodeIds: string[], isOrdered = false) =>
            highlightEdges(nodeIds, setHighlightedEdges, isOrdered),
        highlightedNodes,
        highlightNodeEdges: (nodeId: string, withDescendants = true) =>
            highlightNodeEdges(nodeId, setHighlightedEdges, setHighlightedNodes, withDescendants),
        selectNode: (nodeId?: NodeId, highlightEdges = true, isShiftPressed = false, withDescendants = true) =>
            selectNodeId(
                nodeId,
                isShiftPressed,
                setHighlightedEdges,
                setHighlightedNodes,
                highlightEdges,
                withDescendants,
            ),
        selectedNodeId,
        edgesBySource,
        edgesByTarget,
        nodesById,
        setLayout,
        layout: layoutRef,
        highlightedEdgesSimulated,
        highlightNodes: (nodeIds: string[]) => highlightNodes(nodeIds, setHighlightedNodes),
        highlightEdgesSimulated: (nodeIds: string[], isOrdered = false) =>
            highlightEdges(nodeIds, setHighlightedEdgesSimulated, isOrdered),
        highlightSimulatedNodes: (nodeIds: string[]) => highlightNodes(nodeIds, setHighlightedNodesSimulated),
        accumulateHighlightSimulatedNodes: (nodeIds: string[]) =>
            accumulateHighlightNodes(nodeIds, setHighlightedNodesSimulated),
        accumulateHighlightEdgesSimulated: (nodeIds: string[], isOrdered = false) =>
            accumulateHighlightEdges(nodeIds, setHighlightedEdgesSimulated, isOrdered),
        highlightedNodesSimulated,
        highlightWf0Nodes: (nodeIds: string[]) => highlightNodes(nodeIds, setHighlightedNodesWf0),
        highlightWf0Edges: (nodeIds: string[], isOrdered = false) =>
            highlightEdges(nodeIds, setHighlightedEdgesWf0, isOrdered),
        accumulateHighlightWf0Nodes: (nodeIds: string[]) => accumulateHighlightNodes(nodeIds, setHighlightedNodesWf0),
        accumulateHighlightWf0Edges: (nodeIds: string[], isOrdered = false) =>
            accumulateHighlightEdges(nodeIds, setHighlightedEdgesWf0, isOrdered),
        highlightedNodesWf0,
        highlightedEdgesWf0,
        highlightNodeEdgesSimulated: (nodeId: string, withDescendants = true) =>
            highlightNodeEdges(nodeId, setHighlightedEdgesSimulated, setHighlightedNodesSimulated, withDescendants),
        selectSimulatedNode: (nodeId?: NodeId, highlightEdges = true, isShiftPressed = false) =>
            selectNodeId(
                nodeId,
                isShiftPressed,
                setHighlightedEdgesSimulated,
                setHighlightedNodesSimulated,
                highlightEdges,
                false,
            ),
        selectWf0Node: (nodeId?: NodeId, highlightEdges = true, isShiftPressed = false) =>
            selectNodeId(nodeId, isShiftPressed, setHighlightedEdgesWf0, setHighlightedNodesWf0, highlightEdges, false),
        highlightEdgeById,
        convertNodeToDecision,
        policyMetadata,
        setPolicyMetadata,
    };
};

export type PolicyEditorContext = ReturnType<typeof useGeneratePolicyEditor>;

const policyEditorContext = createContext<PolicyEditorContext | undefined>(undefined);

type PolicyEditorProviderProps = { children: ReactNode };

const PolicyEditorProvider = ({ children }: PolicyEditorProviderProps) => {
    const policy = useGeneratePolicyEditor();
    return <policyEditorContext.Provider value={policy}>{children}</policyEditorContext.Provider>;
};

export const usePolicyEditor = () => {
    const context = useContext(policyEditorContext);
    if (!context) {
        throw Error('"usePolicyEditor" cannot be used outside "PolicyEditorProvider"!');
    }
    return context;
};

export default PolicyEditorProvider;
